import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  TextField,
  IconButton,
  Paper,
  Tooltip,
  Typography,
} from '@mui/material'
import DeleteIcon from '@mui/icons-material/Delete'
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { parse, format, isValid } from 'date-fns'
import { useDataApi } from '../../../../context/DataApiContext'
import AutoCompleteInput from '../../../AutoCompleteInput'

function StatusChip({ errors, warnings, t }) {
  if (errors.length > 0) {
    return (
      <Chip
        size="small"
        color="error"
        label={t('calendar-import.status-error')}
      />
    )
  }
  if (warnings.length > 0) {
    return (
      <Chip
        size="small"
        color="warning"
        label={t('calendar-import.status-warning')}
      />
    )
  }
  return (
    <Chip size="small" color="success" label={t('calendar-import.status-ok')} />
  )
}

function ErrorTooltip({ errors, warnings, t }) {
  const messages = [
    ...errors.map((e) => t(`calendar-import.errors.${e}`, e)),
    ...warnings.map((w) => t(`calendar-import.warnings.${w}`, w)),
  ]
  if (messages.length === 0) return null
  return (
    <Tooltip title={messages.join(' | ')}>
      <span
        style={{ marginLeft: 4, cursor: 'help', color: '#888', fontSize: 12 }}
      >
        ⓘ
      </span>
    </Tooltip>
  )
}

export default function ValidationStep({
  initialRows,
  constructorId,
  onConfirm,
  onBack,
}) {
  const { t } = useTranslation()
  const { dataProvider } = useDataApi()
  const [loading, setLoading] = useState(true)
  const [validatedRows, setValidatedRows] = useState([])
  const [apiError, setApiError] = useState(null)
  const [page, setPage] = useState(0)
  const PAGE_SIZE = 25

  useEffect(() => {
    setLoading(true)
    setApiError(null)
    dataProvider.calendar
      .validateImport(initialRows, constructorId)
      .then((result) => {
        setValidatedRows(result.rows)
      })
      .catch(() => setApiError(t('errors.api-error')))
      .finally(() => setLoading(false))
  }, [])

  const handleDelete = (rowIndex) => {
    setValidatedRows((prev) => {
      const next = prev.filter((r) => r.rowIndex !== rowIndex)
      const maxPage = Math.max(0, Math.ceil(next.length / PAGE_SIZE) - 1)
      setPage((p) => Math.min(p, maxPage))
      return next
    })
  }

  const handleFieldChange = (rowIndex, field, value) => {
    setValidatedRows((prev) =>
      prev.map((r) => (r.rowIndex === rowIndex ? { ...r, [field]: value } : r))
    )
  }

  const handleLocationChange = (rowIndex, location) => {
    setValidatedRows((prev) =>
      prev.map((r) => {
        if (r.rowIndex !== rowIndex) return r
        const errors = r.errors.filter(
          (e) => e !== 'MISSING_LOCATION' && e !== 'UNKNOWN_LOCATION'
        )
        return {
          ...r,
          locationName: location ? location.title : null,
          controlLocationId: location ? location.id : null,
          errors,
        }
      })
    )
  }

  const handleControllerChange = (rowIndex, controller) => {
    setValidatedRows((prev) =>
      prev.map((r) => {
        if (r.rowIndex !== rowIndex) return r
        return {
          ...r,
          controllerName: controller ? controller.title : null,
          controllerId: controller ? controller.id : null,
        }
      })
    )
  }

  const handleRevalidate = () => {
    const rows = validatedRows.map((r) => ({
      nParc: r.nParc || '',
      locationName: r.locationName || '',
      date: r.date || '',
      observations: r.observations || '',
    }))
    setLoading(true)
    setApiError(null)
    dataProvider.calendar
      .validateImport(rows, constructorId)
      .then((result) => setValidatedRows(result.rows))
      .catch(() => setApiError(t('errors.api-error')))
      .finally(() => setLoading(false))
  }

  const errorRows = validatedRows.filter((r) => r.errors.length > 0)
  const warningRows = validatedRows.filter(
    (r) => r.errors.length === 0 && r.warnings.length > 0
  )
  const confirmRows = validatedRows
    .filter((r) => r.errors.length === 0)
    .map((r) => ({
      chassisId: r.chassisId,
      controlLocationId: r.controlLocationId,
      controllerId: r.controllerId || null,
      date: r.date,
    }))

  const canConfirm = confirmRows.length > 0

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    )
  }

  if (apiError) {
    return <Alert severity="error">{apiError}</Alert>
  }

  return (
    <Stack spacing={2}>
      <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
        <Typography variant="body2">
          {validatedRows.length} ligne(s) — {errorRows.length} erreur(s) —{' '}
          {warningRows.length} avertissement(s)
        </Typography>
        <Button size="small" variant="outlined" onClick={handleRevalidate}>
          {t('calendar-import.validate-revalidate')}
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ maxHeight: 420 }}>
        <Table size="small" stickyHeader>
          <TableHead>
            <TableRow>
              <TableCell>{t('calendar-import.validate-col-nparc')}</TableCell>
              <TableCell>
                {t('calendar-import.validate-col-location')}
              </TableCell>
              <TableCell>{t('calendar-import.validate-col-date')}</TableCell>
              <TableCell>
                {t('calendar-import.validate-col-controller')}
              </TableCell>
              <TableCell>
                {t('calendar-import.validate-col-observations')}
              </TableCell>
              <TableCell>{t('calendar-import.validate-col-status')}</TableCell>
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {validatedRows
              .slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
              .map((row) => {
                const rowBg =
                  row.errors.length > 0
                    ? '#fff0f0'
                    : row.warnings.length > 0
                    ? '#fffbe6'
                    : undefined
                return (
                  <TableRow key={row.rowIndex} sx={{ backgroundColor: rowBg }}>
                    <TableCell>
                      <TextField
                        size="small"
                        variant="standard"
                        value={row.nParc || ''}
                        onChange={(e) =>
                          handleFieldChange(
                            row.rowIndex,
                            'nParc',
                            e.target.value
                          )
                        }
                        error={
                          row.errors.includes('MISSING_NPARC') ||
                          row.errors.includes('UNKNOWN_NPARC')
                        }
                        inputProps={{ style: { fontSize: 13 } }}
                      />
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Box
                        sx={{
                          '& input, & .MuiAutocomplete-input': { fontSize: 13 },
                        }}
                      >
                        <AutoCompleteInput
                          resource="control/location-autocomplete"
                          query={constructorId ? { constructorId } : undefined}
                          value={
                            row.controlLocationId
                              ? {
                                  id: row.controlLocationId,
                                  title: row.locationName,
                                }
                              : null
                          }
                          onChange={(val) =>
                            handleLocationChange(row.rowIndex, val)
                          }
                          error={
                            row.errors.includes('MISSING_LOCATION') ||
                            row.errors.includes('UNKNOWN_LOCATION')
                          }
                          canDelete
                        />
                      </Box>
                    </TableCell>
                    <TableCell sx={{ minWidth: 140 }}>
                      <LocalizationProvider dateAdapter={AdapterDateFns}>
                        <DatePicker
                          value={(() => {
                            const d = parse(
                              row.date || '',
                              'yyyy-MM-dd',
                              new Date()
                            )
                            return isValid(d) ? d : null
                          })()}
                          inputFormat="dd/MM/yyyy"
                          disableMaskedInput
                          onChange={(val) => {
                            const str =
                              val && isValid(val)
                                ? format(val, 'yyyy-MM-dd')
                                : ''
                            handleFieldChange(row.rowIndex, 'date', str)
                          }}
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              size="small"
                              variant="standard"
                              error={
                                row.errors.includes('MISSING_DATE') ||
                                row.errors.includes('INVALID_DATE_FORMAT')
                              }
                              inputProps={{
                                ...params.inputProps,
                                style: { fontSize: 13 },
                              }}
                            />
                          )}
                        />
                      </LocalizationProvider>
                    </TableCell>
                    <TableCell sx={{ minWidth: 180 }}>
                      <Box
                        sx={{
                          '& input, & .MuiAutocomplete-input': { fontSize: 13 },
                        }}
                      >
                        <AutoCompleteInput
                          resource="users/autocomplete-with-controls"
                          value={
                            row.controllerId
                              ? {
                                  id: row.controllerId,
                                  title: row.controllerName,
                                }
                              : null
                          }
                          onChange={(val) =>
                            handleControllerChange(row.rowIndex, val)
                          }
                          canDelete
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        variant="standard"
                        value={row.observations || ''}
                        onChange={(e) =>
                          handleFieldChange(
                            row.rowIndex,
                            'observations',
                            e.target.value
                          )
                        }
                        inputProps={{ style: { fontSize: 13 } }}
                      />
                    </TableCell>
                    <TableCell>
                      <Box display="flex" alignItems="center">
                        <StatusChip
                          errors={row.errors}
                          warnings={row.warnings}
                          t={t}
                        />
                        <ErrorTooltip
                          errors={row.errors}
                          warnings={row.warnings}
                          t={t}
                        />
                      </Box>
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(row.rowIndex)}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                )
              })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={validatedRows.length}
        page={page}
        onPageChange={(_, newPage) => setPage(newPage)}
        rowsPerPage={PAGE_SIZE}
        rowsPerPageOptions={[PAGE_SIZE]}
      />

      <Box display="flex" justifyContent="space-between">
        <Button variant="outlined" onClick={onBack}>
          {t('common.back')}
        </Button>
        <Button
          variant="contained"
          color="primary"
          disabled={!canConfirm}
          onClick={() => onConfirm(confirmRows)}
        >
          {t('calendar-import.validate-confirm')} ({confirmRows.length})
        </Button>
      </Box>
    </Stack>
  )
}
